import { getListIds, useCampaignSendSize } from 'modules/campaigns/lib/hooks';
import { getIsWhatsAppCampaign } from 'modules/campaigns/lib/utils';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import { Button, BUTTON_VARIANTS } from 'ui/button';
import { getIsEligibilityCampaign } from '../../components/utils';
import { useCampaignContext, useEditorContext } from '../../context';
import {
  getUpdateCampaignAudienceMutationPayload,
  useSaveCampaign,
  useUpdateCampaignMutation,
} from '../../hooks';
import { CampaignEditorFormState, CampaignStep } from '../../types';
import {
  BodyHeader,
  BodyWrapper,
  FooterWrapper,
  StyledTakeover,
  TakeoverHeaderBarContent,
} from '../Takeovers.styled';
import { AudienceSelectionPaginationFooter } from './AudienceSelection.styled';
import { AudienceSelectionHeader, NoListsEmptyState } from './components';
import { AudienceSelectionHeaderEligibility } from './components/Header/AudienceSelectionHeaderEligibility';
import { AudienceSelectionTable } from './components/Table';
import { useAudienceContext } from './context';
import { useCheckListsExist } from './hooks';

export const USE_SELECTED_LISTS_BTN_TEST_ID = 'use-selected-lists-btn';

export const AudienceSelection = () => {
  const dispatch = useDispatch();
  const { saveCampaign, isLoading: isSavingListsREST } = useSaveCampaign();
  const { updateCampaign, isLoading: isSavingListsGraphQL } = useUpdateCampaignMutation();
  const { viewCampaignStep, isAudienceTakeoverOpen, setIsAudienceTakeoverOpen } =
    useEditorContext();
  const {
    refetch: refetchCampaign,
    campaign,
    estimatedSendSizeData,
    expectedSendSizeData,
  } = useCampaignContext();
  const {
    selectedSendLists,
    selectedSuppressionLists,
    pagination,
    errorMessage,
    isLoading,
    handlePageChange,
  } = useAudienceContext();
  const isEligibility = getIsEligibilityCampaign(campaign.campaignTypeDefinition);
  const { page, limit, count } = pagination;
  const listsExist = useCheckListsExist(count);
  const hasNoSelection = selectedSendLists.length === 0;
  const isWhatsAppCampaign = getIsWhatsAppCampaign(campaign.messageMedium);
  const isSavingLists = isWhatsAppCampaign ? isSavingListsGraphQL : isSavingListsREST;

  const { refetch: refetchSendSize } = useCampaignSendSize({
    estimatedSendSizeData,
    expectedSendSizeData,
  });

  const handleClose = () => {
    setIsAudienceTakeoverOpen(false);
    viewCampaignStep(CampaignStep.Audience);
  };

  const onSuccess = () => {
    setIsAudienceTakeoverOpen(false);
    refetchCampaign();
    refetchSendSize();
    viewCampaignStep(CampaignStep.Audience);
    viewCampaignStep(CampaignStep.AudienceEligibility);
  };

  const handleSaveSelectedLists = () => {
    if (isWhatsAppCampaign) {
      // GraphQL - newly introduced for WhatsApp
      updateCampaign({
        variables: getUpdateCampaignAudienceMutationPayload(campaign.id, {
          listIds: getListIds(selectedSendLists),
          suppressionListIds: getListIds(selectedSuppressionLists),
        }),
        onCompleted: () => {
          dispatch(addPositiveToast(`${campaign.name} was successfully updated.`));
          onSuccess();
        },
        onError: (e) => {
          dispatch(
            addNegativeToastWithErrorId(
              `Error saving ${campaign.name}. Please try again later.`,
              'saveCampaign',
              e,
            ),
          );
        },
      });
    } else {
      // REST - currently used for all other campaign mediums
      saveCampaign(
        {
          listIds: getListIds(selectedSendLists),
          suppressionListIds: getListIds(selectedSuppressionLists),
        } as unknown as CampaignEditorFormState,
        onSuccess,
      );
    }
  };

  /**
   * takeover is closed
   */
  if (!isAudienceTakeoverOpen) return null;

  return (
    <StyledTakeover
      isOverlay
      onClose={handleClose}
      HeaderBarContent={<TakeoverHeaderBarContent>Audience selection</TakeoverHeaderBarContent>}
      Body={
        listsExist || errorMessage || isLoading ? (
          <BodyWrapper>
            <BodyHeader>{isEligibility ? 'Dynamic lists' : 'Lists'}</BodyHeader>
            {isEligibility ? <AudienceSelectionHeaderEligibility /> : <AudienceSelectionHeader />}
            <AudienceSelectionTable />
            <AudienceSelectionPaginationFooter
              currentPage={page}
              itemsPerPage={limit}
              numItems={count}
              onUpdate={handlePageChange}
            />
          </BodyWrapper>
        ) : (
          <NoListsEmptyState />
        )
      }
      Footer={
        <FooterWrapper>
          <Button
            isLoading={isSavingLists}
            variant={BUTTON_VARIANTS.PRIMARY}
            onClick={handleSaveSelectedLists}
            isDisabled={hasNoSelection || !!errorMessage || isSavingLists}
            data-test={USE_SELECTED_LISTS_BTN_TEST_ID}
          >
            Use selected list
          </Button>
        </FooterWrapper>
      }
    />
  );
};
